/**
 * The one button. Four tones, so a screen can have exactly one primary and make
 * everything else visibly secondary. Sizes meet 36px on desktop, 44px on touch.
 */

import type { ButtonHTMLAttributes } from "react";

type Tone = "primary" | "secondary" | "ghost" | "danger";

const tones: Record<Tone, string> = {
  primary:
    "bg-brand-600 text-white shadow-sm hover:bg-brand-700 disabled:bg-brand-600/50 dark:bg-brand-500 dark:hover:bg-brand-400 dark:disabled:bg-brand-500/40",
  secondary:
    "bg-white text-zinc-800 ring-1 ring-zinc-300 shadow-xs hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-100 dark:ring-zinc-700 dark:hover:bg-zinc-800",
  ghost:
    "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100",
  danger:
    "bg-red-600 text-white shadow-sm hover:bg-red-700 disabled:bg-red-600/50 dark:bg-red-500 dark:hover:bg-red-400",
};

export const focusRing =
  "outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950";

export function Button({
  tone = "secondary",
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: Tone }) {
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-lg px-3.5 text-sm font-medium whitespace-nowrap transition-colors disabled:cursor-not-allowed pointer-coarse:h-11 ${focusRing} ${tones[tone]} ${className}`}
    />
  );
}
